import { ConnectedPosition, OverlayModule } from '@angular/cdk/overlay';
import { afterNextRender, ChangeDetectionStrategy, Component, computed, ElementRef, forwardRef, inject, Injector, input, signal, viewChild, ViewEncapsulation } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { ALL_DESIGN_TONES, DesignTone } from '@workbuddy/ui/v2/design-system';

@Component({
  selector: 'wb-tone-picker',
  standalone: true,
  imports: [OverlayModule],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrls: ['./tone.picker.component.less'],
  templateUrl: './tone.picker.component.html',
  providers: [{ provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => TonePickerComponent), multi: true }],
})
export class TonePickerComponent implements ControlValueAccessor {

  public readonly $tones = input<DesignTone[]>(ALL_DESIGN_TONES, { alias: 'tones' });
  public readonly $columns = input<number>(3, { alias: 'columns' });

  protected readonly $value = signal<DesignTone>('indigo');
  protected readonly $isOpen = signal(false);
  protected readonly $disabled = signal(false);
  protected readonly $activeIndex = signal(0);

  protected readonly $swatches = computed(() => this.$tones().map(tone => ({ tone })));

  private readonly overlayRoot = viewChild<ElementRef<HTMLElement>>('overlayRoot');
  private readonly triggerEl = viewChild<ElementRef<HTMLButtonElement>>('triggerEl');

  protected readonly positions: ConnectedPosition[] = [
    // bottom-left aligned
    { originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top', offsetY: 6 },
    // bottom-right aligned (when trigger sits near the right edge)
    { originX: 'end', originY: 'bottom', overlayX: 'end', overlayY: 'top', offsetY: 6 },
    // top-left aligned
    { originX: 'start', originY: 'top', overlayX: 'start', overlayY: 'bottom', offsetY: -6 },
    // top-right aligned
    { originX: 'end', originY: 'top', overlayX: 'end', overlayY: 'bottom', offsetY: -6 },
  ];

  private onChange: (value: DesignTone) => void = () => {};
  private onTouched: () => void = () => {};

  public writeValue(value: DesignTone | null | undefined): void {

    const tones = this.$tones();
    const fallback = tones[0] ?? 'indigo';
    this.$value.set(value && tones.includes(value) ? value : fallback);
  }

  public registerOnChange(fn: (value: DesignTone) => void): void {

    this.onChange = fn;
  }

  public registerOnTouched(fn: () => void): void {

    this.onTouched = fn;
  }

  public setDisabledState(isDisabled: boolean): void {

    this.$disabled.set(isDisabled);
  }

  protected toggle(): void {

    if (this.$disabled()) return;
    this.$isOpen.update(open => !open);
  }

  protected close(): void {

    this.$isOpen.set(false);
    this.onTouched();
  }

  protected onSelect(tone: DesignTone): void {

    if (this.$disabled()) return;
    this.$value.set(tone);
    this.onChange(tone);
    this.close();
    this.restoreTriggerFocus();
  }

  private restoreTriggerFocus(): void {

    setTimeout(() => this.triggerEl()?.nativeElement?.focus(), 0);
  }

  protected onAttached(): void {

    afterNextRender(() => {
      // Position active on the currently selected tone.
      const tones = this.$tones();
      const idx = Math.max(0, tones.indexOf(this.$value()));
      this.$activeIndex.set(idx);
      this.overlayRoot()?.nativeElement?.focus();
    }, { injector: this.injector });
  }

  protected setActive(index: number): void {

    this.$activeIndex.set(index);
  }

  protected onOverlayKeydown(event: KeyboardEvent): void {

    const total = this.$tones().length;
    if (!total && event.key !== 'Escape') return;

    const cols = this.$columns();
    const idx = this.$activeIndex();

    switch (event.key) {

      case 'Enter':
      case ' ': {
        event.preventDefault();
        event.stopPropagation();
        this.onSelect(this.$tones()[idx]);
        break;
      }
      case 'Escape': {
        event.preventDefault();
        event.stopPropagation();
        this.close();
        this.restoreTriggerFocus();
        break;
      }
      case 'ArrowRight': {
        event.preventDefault();
        this.$activeIndex.set((idx + 1) % total);
        break;
      }
      case 'ArrowLeft': {
        event.preventDefault();
        this.$activeIndex.set((idx - 1 + total) % total);
        break;
      }
      case 'ArrowDown': {
        event.preventDefault();
        const next = idx + cols;
        this.$activeIndex.set(next < total ? next : idx);
        break;
      }
      case 'ArrowUp': {
        event.preventDefault();
        const prev = idx - cols;
        this.$activeIndex.set(prev >= 0 ? prev : idx);
        break;
      }
      case 'Home': {
        event.preventDefault();
        this.$activeIndex.set(0);
        break;
      }
      case 'End': {
        event.preventDefault();
        this.$activeIndex.set(total - 1);
        break;
      }
      case 'Tab': {
        this.close();
        break;
      }
    }
  }

  private readonly injector = inject(Injector);
}
